import TitleBar from '../../../components/TitleBar';
import { Button, CommentBox } from '../../../components';
import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import QuickEdit from '../../../components/assets/QuickEdit';
import { Modal as AntModal } from 'antd';
import { Footer } from '../../../components/common/Footer';
import {
  GET_MITIGATION_PROCESS,
  GET_MITIGATION_PROCESS_AREA,
} from '../queries/processMitigation';
import { useQuery } from '@apollo/client';
import { UpdateMitigation } from './UpdateMitigation';
import { CommentaryModal } from './CommentaryModal';
import { Commentary } from './Commentary';
import _ from 'lodash';
import ModalHeader from '../../../components/generic/ModalHeader';
import FinancialData from '../../../components/common/FinancialData';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import QueryErrorBoundary from '../../../components/error-handling';
import { useUserContext } from '../../../context/PermissionsContext';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import { baseColumns } from './exportColumns';
import PercentageFormatter from '../../../components/common/PercentageFormatter';
import TitleUpdater from '../../../components/common/TitleUpdater';

type Mitigation = {
  key: string;
  externalDelay: number;
  externalPercentage: number;
  internalDelay: number;
  internalPercentage: number;
  onsiteDelay: number;
  onsitePercentage: number;
  strategyComment: string;
};

export type ProcessMitigation = {
  name: string;
  dependencySupportRevenue: number;
  mitigation: Mitigation;
  key: string;
  children?: ProcessMitigation[];
  intermediates?: ProcessMitigation[];
  __typename: string;
};

export type FinancialType =
  | 'division'
  | 'productGroup'
  | 'product'
  | 'intermediate';

export function PAMitigation() {
  const params = useParams();
  const locationId = params.locationId!;
  const processAreaId = params.processAreaId!;
  const mitigationType = params.mitigationType!;
  let [searchParams] = useSearchParams();
  const locationName = searchParams.get('location')!;
  const processArea = searchParams.get('processArea')!;
  const [openModal, setOpenModal] = React.useState(false);
  const [openComment, setOpenComment] = React.useState(false);
  const [item, setItem] = React.useState<ProcessMitigation>();
  const [financialType, setFinancialType] =
    React.useState<FinancialType>('division');
  const [comment, setComment] = React.useState('');
  const [strategyId, setStrategyId] = React.useState('');

  const mutation =
    mitigationType === 'process-area-mitigation'
      ? GET_MITIGATION_PROCESS_AREA
      : GET_MITIGATION_PROCESS;

  const vars =
    mitigationType === 'process-area-mitigation'
      ? {
          locationUUID: locationId,
          processAreaUUID: processAreaId,
        }
      : {
          locationUUID: locationId,
          processUUID: processAreaId,
        };

  const { canViewFinancials } = useUserContext();

  const { data, loading, error } = useQuery(mutation, {
    variables: {
      data: vars,
    },
  });

  const dataArr =
    mitigationType === 'process-area-mitigation'
      ? data?.getMitigationQuantificationPerProcessArea!
      : data?.getMitigationQuantificationPerProcess!;

  const getFinancialType = (record: ProcessMitigation) => {
    if (
      record.__typename === 'MitigationsByProcessAndLocationModel' ||
      record.__typename === 'MitigationsByProcessAreaAndLocationModel'
    ) {
      return 'division';
    } else if (
      record.__typename === 'ProductGroups' ||
      record.__typename === 'MitigationProductGroups'
    ) {
      return 'productGroup';
    } else {
      return 'product';
    }
  };

  const handleMitigationEdit = (record: ProcessMitigation) => {
    setFinancialType(getFinancialType(record));
    setOpenModal(true);
    setItem(record);
  };

  const columns: ColumnsType<any> = [
    {
      dataIndex: 'name',
      key: 'name',
      width: 150,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text) => <span className="custom-table--column">{text}</span>,
    },
    {
      title: 'Revenue Supported',
      dataIndex: 'dependencySupportRevenue',
      key: 'dependencySupportRevenue',
      width: 80,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) =>
            tableSortingMethods.number(
              a.dependencySupportRevenue,
              b.dependencySupportRevenue,
            )
        : undefined,
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    {
      title: 'Onsite Mitigation',
      dataIndex: 'onsiteDelay',
      key: 'onsiteDelay',
      width: 80,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.onsiteDelay, b.onsiteDelay),
      render: (_, record) => {
        const { onsitePercentage = 0, onsiteDelay = 0 } =
          record?.mitigation || {};
        return (
          <div className="custom-table--column">
            <PercentageFormatter value={onsitePercentage} />
            <span
              className={`text-sm ${onsiteDelay === 0 ? 'text-[#CDCDCD]' : ''}`}
            >
              {onsiteDelay} mths
            </span>
          </div>
        );
      },
    },
    {
      title: 'Internal Mitigation',
      dataIndex: 'internalDelay',
      key: 'internalDelay',
      width: 80,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.internalDelay, b.internalDelay),
      render: (_, record) => {
        const { internalPercentage = 0, internalDelay = 0 } =
          record?.mitigation || {};
        return (
          <div className="custom-table--column">
            <PercentageFormatter value={internalPercentage} />
            <span
              className={`text-sm ${
                internalDelay === 0 ? 'text-[#CDCDCD]' : ''
              }`}
            >
              {internalDelay} mths
            </span>
          </div>
        );
      },
    },
    {
      title: 'External Mitigation',
      dataIndex: 'externalDelay',
      key: 'externalDelay',
      width: 80,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.externalDelay, b.externalDelay),
      render: (_, record) => {
        const { externalPercentage = 0, externalDelay = 0 } =
          record?.mitigation || {};
        return (
          <div className="custom-table--column">
            <PercentageFormatter value={externalPercentage} />
            <span
              className={`text-sm ${
                externalDelay === 0 ? 'text-[#CDCDCD]' : ''
              }`}
            >
              {externalDelay} mths
            </span>
          </div>
        );
      },
    },
  ];

  const extendActions = (record: any) => {
    return (
      <div className="w-full inline-flex flex-row justify-between items-center gap-3">
        <CommentBox text={record.mitigation?.strategyComment} />
        <Button
          type={'edit'}
          onClick={() => handleMitigationEdit(record)}
          className="w-8 h-7 mx-0"
        >
          <QuickEdit />
        </Button>
      </div>
    );
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  const getComment = (value: string) => setComment(value);

  const normalizedData = (data: any) => {
    const formatedData = data?.map((item: any) => {
      const { productGroups, ...rest } = item;
      return {
        key: item.uuid,
        ...rest,
        ...(item.productGroups?.length > 0
          ? {
              children: item.productGroups?.map((productGroup: any) => {
                const { products, ...rest } = productGroup;
                return {
                  key: productGroup.uuid,
                  ...rest,
                  ...(productGroup.products?.length > 0
                    ? {
                        children: productGroup.products?.map((product: any) => {
                          return {
                            key: product.uuid,
                            ...product,
                          };
                        }),
                      }
                    : {}),
                };
              }),
            }
          : {}),
      };
    });

    return formatedData;
  };

  const formatedData = normalizedData(dataArr);

  return (
    <div>
      <TitleUpdater title={`${processArea} Mitigation`} />
      <TitleBar
        title="Process Area & Process Mitigation"
        subtitle={`${locationName} // ${processArea}`}
        brief="In this section add any mitigation that is available for this process area."
      />
      <div className="grid gap-6">
        <Commentary
          processId={processAreaId}
          mitigationType={mitigationType}
          getComment={getComment}
          updateStrategyId={(strategyId: string) => setStrategyId(strategyId)}
          onEdit={() => setOpenComment(true)}
        />
        <QueryErrorBoundary error={error}>
          <TableV3
            title={`Mitigation Quantification for ${processArea}`}
            columns={columns}
            data={formatedData}
            loading={loading}
            extendActions={extendActions}
            customStyles="custom-table--dependency-mapping custom-column-PA--heading nested-columns--custom"
            exportExcelColumns={baseColumns}
          />
        </QueryErrorBoundary>
      </div>
      <AntModal
        open={openModal}
        onCancel={closeModal}
        title={<ModalHeader title={`${processArea} Mitigation`} />}
        footer={null}
        className="custom-modal"
        width={580}
        centered
        bodyStyle={{
          padding: '0px 24px 24px',
        }}
      >
        <h2 className="text-black tracking-ultraWidest text-base font-bold">
          {item?.name}
        </h2>
        <UpdateMitigation
          item={item!}
          financialType={financialType}
          locationId={locationId}
          processId={processAreaId}
          mitigationType={mitigationType}
          onClose={closeModal}
        />
      </AntModal>
      <AntModal
        open={openComment}
        onCancel={() => setOpenComment(false)}
        title={<ModalHeader title={`${processArea} Mitigation Commentary`} />}
        width={1000}
        footer={null}
        className="custom-modal"
      >
        <CommentaryModal
          strategyId={strategyId}
          mitigationType={mitigationType}
          onClose={() => setOpenComment(false)}
          comment={comment}
        />
      </AntModal>
      <Footer goBack />
    </div>
  );
}
